import {AfterViewInit, Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, ParamMap, Router} from '@angular/router';
import {EdsIdMapperService} from '../services/eds-id-mapper/eds-id-mapper.service';
import {Observable, of} from 'rxjs';
import {mergeMap} from 'rxjs/operators';
import {TvShopEntertainmentPack} from '../model/tv-shop-entertainment-pack';
import {NavigableComponent} from '../model/navigable-component';
import {NavigationService} from '../services/navigation/navigation.service';
import {PageNames} from '../shared/page-names.enum';
import {ErrorMessageConstants} from '../shared/error-message-constants';
import {TvShopStateService} from '../services/state/tv-shop-state.service';

@Component({
  selector: 'app-deep-linking',
  templateUrl: './deep-linking.component.html',
  styleUrls: ['./deep-linking.component.less']
})
export class DeepLinkingComponent extends NavigableComponent implements OnInit, OnDestroy, AfterViewInit {

  packs: Array<TvShopEntertainmentPack> = [];
  titleType: string;

  constructor(private edsIdMapper: EdsIdMapperService,
              private route: ActivatedRoute,
              private router: Router,
              private stateService: TvShopStateService,
              protected navigationService: NavigationService) {
    super(navigationService);
  }

  ngAfterViewInit(): void {
    this.navigationService.resetNavigation();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.navigationService.clearNavigation();
  }

  ngOnInit() {
    this.stateService.setLoading(true);
    this.processPacks();
    this.navigationService.setCurrentPageComponent(PageNames.DEEP_LINKING);
  }

  private processPacks(): void {
    const entertainmentPacks = this.stateService.selectSnapshot(state => state.tvShopEntertainmentPacks);
    this.mapParametersAndGetOmapiIds().subscribe((omapiIds: string[]) => {
        const filteredEntertainmentPacks = this.filterPacksByOmapiId(entertainmentPacks, omapiIds);
        if (filteredEntertainmentPacks.length === 1) {
          this.stateService.setSelectedTvShopEntertainmentPack(filteredEntertainmentPacks[0]);
          this.router.navigateByUrl(`/product-details/${filteredEntertainmentPacks [0].omapiId}` + '?origin=deeplink-direct');
        } else {
          if (filteredEntertainmentPacks.length > 2) {
            filteredEntertainmentPacks.splice(filteredEntertainmentPacks.length - 2, filteredEntertainmentPacks.length);
          }
          this.packs = filteredEntertainmentPacks;
        }
        this.stateService.setLoading(false);
      }, error => {
        this.stateService.handleError(ErrorMessageConstants.ERROR_FETCHING_ENTERTAINMENT_PACKS, error);
      }
    );
  }

  private mapParametersAndGetOmapiIds(): Observable<string[]> {
    return this.route.queryParamMap.pipe(
      mergeMap((params: ParamMap) => {
        this.titleType = params.get('Type') ? params.get('Type') : '';

        const edsProductIdParamKey = this.compareCaseInsensitiveAndGetKey(params);
        const unProcessedEdsProductIds = params.get(edsProductIdParamKey);

        if (unProcessedEdsProductIds) {
          const ids = this.splitParamsOnSpecialCharactersAndFilterOutNaN(unProcessedEdsProductIds);
          return this.edsIdMapper.mapEdsIdsToOmapiIds(ids);
        }
        return of([]);
      }));
  }

  private filterPacksByOmapiId(entertainnmentPacks: Array<TvShopEntertainmentPack>, omapiIds: string[]) {
    const packsToCompare = [];
    omapiIds.forEach(omapiId => {
      const entertainmentPack = entertainnmentPacks.filter(pack => pack.omapiId === omapiId + '').pop();
      if (entertainmentPack) {
        packsToCompare.push(entertainmentPack);
      }
    });
    return packsToCompare;
  }

  splitParamsOnSpecialCharactersAndFilterOutNaN(unProcessedEdsProductIds): string[] {
    const splitted = (unProcessedEdsProductIds + '').split(/[ %,.\-_]+/);
    return splitted.filter(id => !isNaN(+id));
  }

  private compareCaseInsensitiveAndGetKey(params: ParamMap): string {
    return params.keys.filter(paramName => (paramName + '')
      .toLowerCase()
      .trim() === 'edsproductids'
    ).pop();
  }
}
